<template>
  <section v-if="canManage && available" class="mt-6" data-testid="megafon-incoming-setup">
    <h3 class="text-subtitle-1 font-weight-medium mb-2">{{ $t('megafon.incoming_setup') }}</h3>
    <p class="text-body-2">{{ $t('megafon.incoming_setup_intro') }}</p>
    <v-alert v-if="error" type="error" outlined dense>{{ error }}</v-alert>
    <v-progress-linear v-if="loading" indeterminate class="mb-3" />
    <template v-if="loaded">
      <p v-if="state.last_received_at" class="caption">{{ $t('megafon.incoming_last_received', { date: new Date(state.last_received_at).toLocaleString() }) }}</p>
      <template v-if="state.enabled">
        <v-text-field :value="state.url" :label="$t('megafon.incoming_url')" type="password" outlined readonly hide-details autocomplete="off" />
        <div class="d-flex flex-wrap align-center my-2">
          <v-btn text color="primary" :disabled="busy || !state.url" @click="copyUrl"><v-icon left small>mdi-content-copy</v-icon>{{ copied ? $t('megafon.url_copied') : $t('megafon.copy_url') }}</v-btn>
          <v-btn text :disabled="busy" @click="confirmRotate = true">{{ $t('megafon.rotate_url') }}</v-btn>
          <v-btn text color="error" :loading="busy" @click="mutate('disable')">{{ $t('megafon.disable_incoming') }}</v-btn>
        </div>
        <ol class="text-body-2 pl-5">
          <li>{{ $t('megafon.native_connector_setup') }}</li>
          <li>{{ $t('megafon.native_call_routing') }}</li>
          <li>{{ $t('megafon.native_test_call') }}</li>
        </ol>
      </template>
      <v-btn v-else outlined color="primary" :loading="busy" @click="mutate('enable')">{{ $t('megafon.enable_incoming') }}</v-btn>
      <p class="caption mt-3">{{ $t('megafon.incoming_shared_access') }}</p>
    </template>
    <v-dialog v-model="confirmRotate" max-width="440">
      <v-card><v-card-title>{{ $t('megafon.rotate_url') }}</v-card-title><v-card-text>{{ $t('megafon.rotate_url_warning') }}</v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="confirmRotate = false">{{ $t('megafon.cancel') }}</v-btn><v-btn text color="primary" :loading="busy" @click="mutate('rotate')">{{ $t('megafon.rotate_url') }}</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import { copyToClipboard } from '@/utils/clipboard';
const BASE = '/api/v1/integrations/megafon/incoming';
export default {
  name: 'MegafonIncomingSetup',
  props: { companyId: { type: [Number, String], required: true }, canManage: { type: Boolean, default: false } },
  data: () => ({ state: {}, loading: false, loaded: false, available: true, busy: false, error: '', copied: false, confirmRotate: false, requestVersion: 0 }),
  watch: {
    companyId() { this.state = {}; this.loaded = false; this.load(); },
    canManage(value) { if (!value) { this.requestVersion += 1; this.state = {}; this.loaded = false; } else this.load(); },
  },
  mounted() { this.load(); },
  beforeDestroy() { this.requestVersion += 1; this.state = {}; },
  methods: {
    config() { return { headers: { Authorization: this.$store.getters.getAuthToken } }; },
    async load() {
      if (!this.canManage) return;
      const version = ++this.requestVersion;
      this.loading = true; this.error = '';
      try {
        const { data } = await this.axios.get(BASE, this.config());
        if (version === this.requestVersion) { this.state = data || {}; this.loaded = true; this.available = true; }
      } catch (error) {
        if (version !== this.requestVersion) return;
        if (error.response && error.response.status === 404) this.available = false;
        else this.error = this.$t('megafon.request_error');
      } finally { if (version === this.requestVersion) this.loading = false; }
    },
    async mutate(action) {
      if (!this.canManage || this.busy || !this.loaded) return;
      const version = ++this.requestVersion;
      this.busy = true; this.error = ''; this.copied = false;
      const body = { expected_company_id: this.companyId, generation: this.state.generation };
      try {
        const response = action === 'disable'
          ? await this.axios.delete(BASE, { ...this.config(), params: body })
          : await this.axios.post(action === 'rotate' ? `${BASE}/rotate` : BASE, body, this.config());
        if (version !== this.requestVersion) return;
        this.state = response.data || {}; this.confirmRotate = false; this.$emit('changed', { generation: this.state.generation });
      } catch (error) {
        if (version === this.requestVersion) {
          const code = error.response && error.response.data && error.response.data.error && error.response.data.error.code;
          this.error = code === 'callback_setup_required'
            ? this.$t('megafon.route_numbers_required', { field: this.$t('megafon.route_numbers') })
            : this.$t('megafon.incoming_save_error');
        }
      }
      finally { if (version === this.requestVersion) this.busy = false; }
    },
    async copyUrl() {
      if (!this.canManage || !this.state.url) return;
      try { await copyToClipboard(this.state.url); this.copied = true; }
      catch (_) { this.error = this.$t('megafon.copy_url_error'); }
    },
  },
};
</script>
